import {
  Button,
  Container,
  ContentLayout,
  Grid,
  Header,
  SpaceBetween,
} from '@cloudscape-design/components';
import { Formik } from 'formik';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import LabelFieldsSection from './components/labelFieldsSection';
import { renderLabelBlob } from './components/labelPdf';
import LabelPreview from './components/labelPreview';
import PrintInstructions from './components/printInstructions';
import TravelerScanSection from './components/travelerScanSection';
import loadLabelData from './loadLabelData';

const IS_SAFARI = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export default function PrintProductLabelPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const travelerId = searchParams.get('travelerId');

  const [labelData, setLabelData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const handleTravelerId = useCallback((id) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('travelerId', id);
      return next;
    });
  }, [setSearchParams]);

  useEffect(() => {
    if (!travelerId) {
      setLabelData(null);
      return undefined;
    }
    let cancelled = false;
    // Clear the previous traveler's data so the form never shows (or prints)
    // stale values while the new traveler is loading.
    setLabelData(null);
    setLoading(true);
    setLoadError(null);
    loadLabelData(travelerId)
      .then((data) => { if (!cancelled) setLabelData(data); })
      .catch((error) => {
        if (!cancelled) {
          setLoadError(error.message);
          setLabelData(null);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [travelerId]);

  const initialValues = useMemo(() => ({
    name: labelData?.product?.name || '',
    sku: labelData?.product?.sku || '',
    batch: labelData?.batch || '',
    qrHeader: labelData?.product?.labelQrHeader || '',
    qrValue: labelData?.product?.labelQrUrl || '',
  }), [labelData]);

  // The print iframe must outlive the print dialog, so it's cleaned up lazily:
  // on the next print or on unmount.
  const printFrameRef = useRef(null);
  const cleanupPrintFrame = useCallback(() => {
    if (printFrameRef.current) {
      const { iframe, url } = printFrameRef.current;
      iframe.remove();
      URL.revokeObjectURL(url);
      printFrameRef.current = null;
    }
  }, []);
  useEffect(() => cleanupPrintFrame, [cleanupPrintFrame]);

  // Universal escape hatch: show the PDF in its own tab so it can be printed
  // from any browser's native viewer. The tab is opened synchronously in the
  // click gesture so popup blockers allow it.
  const handleOpenPdf = useCallback(async (values) => {
    const pdfTab = window.open('', '_blank');
    try {
      const blob = await renderLabelBlob(values, travelerId);
      pdfTab.location = URL.createObjectURL(blob);
    } catch (error) {
      pdfTab.close();
      throw error;
    }
  }, [travelerId]);

  const [printing, setPrinting] = useState(false);
  const handlePrint = useCallback(async (values) => {
    // Safari can't print a PDF from a hidden iframe (it rasterizes the frame
    // and drops embedded-font text), so open the PDF in its own tab instead.
    // The tab must be opened synchronously in the click gesture — before any
    // await — or Safari's popup blocker eats it.
    const pdfTab = IS_SAFARI ? window.open('', '_blank') : null;
    setPrinting(true);
    try {
      const blob = await renderLabelBlob(values, travelerId);
      const url = URL.createObjectURL(blob);

      if (pdfTab) {
        pdfTab.location = url;
        return;
      }

      cleanupPrintFrame();
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.title = 'Print product label';
      document.body.appendChild(iframe);
      printFrameRef.current = { iframe, url };
      await new Promise((resolve) => {
        iframe.onload = resolve;
        iframe.src = url;
      });
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch {
        // Last-resort fallback: open the PDF in a new tab.
        window.open(url, '_blank');
      }
    } finally {
      setPrinting(false);
    }
  }, [cleanupPrintFrame, travelerId]);

  return (
    <ContentLayout header={<Header variant="h1">Print Product Label</Header>}>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handlePrint}
      >
        {({ values, setFieldValue }) => (
          <form onSubmit={(event) => event.preventDefault()}>
            <Grid
              gridDefinition={[
                { colspan: { default: 12, s: 7 } },
                { colspan: { default: 12, s: 5 } },
              ]}
            >
              <SpaceBetween size="l">
                <TravelerScanSection
                  travelerId={travelerId}
                  loading={loading}
                  loadError={loadError}
                  warnings={labelData?.warnings || []}
                  loaded={!!labelData}
                  onTravelerId={handleTravelerId}
                />
                <LabelFieldsSection values={values} setFieldValue={setFieldValue} />
              </SpaceBetween>
              <Container
                header={(
                  <Header
                    variant="h2"
                    actions={(
                      <SpaceBetween direction="horizontal" size="xs">
                        <Button
                          formAction="none"
                          iconName="external"
                          onClick={() => handleOpenPdf(values)}
                        >
                          Open PDF
                        </Button>
                        <Button
                          variant="primary"
                          formAction="none"
                          loading={printing}
                          onClick={() => handlePrint(values)}
                        >
                          Print
                        </Button>
                      </SpaceBetween>
                    )}
                  >
                    Preview
                  </Header>
                )}
              >
                <SpaceBetween size="l">
                  <LabelPreview values={values} travelerId={travelerId} />
                  <PrintInstructions />
                </SpaceBetween>
              </Container>
            </Grid>
          </form>
        )}
      </Formik>
    </ContentLayout>
  );
}
